import { BaseSelect, ModalWrapper } from 'modules/shared/components';
import { EMPTY_SINGLE_VALUE } from 'modules/shared/components/form/Select/select.constants';
import { getErrorByPath } from 'modules/shared/utils';
import { useEffect, useMemo, useRef } from 'react';
import { useFormContext, useFormState } from 'react-hook-form';
import { EMPLOYEE_SCHEDULE_FIELDS, TASK_ASSIGNMENT_ERRORS } from '../../constants';
import { useEmployeeAvailability } from '../hooks/useEmployeeAvailability';
import { useEmployeeTaskScheduleModal } from '../hooks/useEmployeeTaskScheduleModal';
import EmployeeTaskScheduler from './EmployeeTaskScheduler';

/**
 * EmployeeAssignmentField
 *
 * A field for selecting an employee and a week.
 * Side effects:
 * - Opens schedule modal when employee is selected without week
 *
 * @param {Object} props
 * @param {boolean} props.disabled
 * @param {Array<Object>} props.employees
 */
export default function EmployeeAssignmentField({ disabled, employees }) {
  const { watch, setValue, clearErrors } = useFormContext();

  const { errors } = useFormState();
  const employeeErrorMessage = getErrorByPath(errors, TASK_ASSIGNMENT_ERRORS.EMPLOYEE);

  const employeeId = watch(EMPLOYEE_SCHEDULE_FIELDS.EMPLOYEE_ID);
  const isoWeek = watch(EMPLOYEE_SCHEDULE_FIELDS.ISO_WEEK);

  const { getAvailabilityForWeek, loading: employeeAvailabilityLoading } =
    useEmployeeAvailability(employeeId);
  const selectedAvailability = employeeId && isoWeek ? getAvailabilityForWeek(isoWeek) : null;

  const employeeMap = useMemo(() => new Map(employees.map((e) => [e.id, e])), [employees]);

  const options = useMemo(
    () => employees.map((e) => ({ label: e.name, value: e.id })),
    [employees],
  );

  const modal = useEmployeeTaskScheduleModal();
  const { open, openModal, close, confirm } = modal;

  const isSelectingRef = useRef(false);

  const resetEmployeeSelection = () => {
    setValue(EMPLOYEE_SCHEDULE_FIELDS.EMPLOYEE_ID, null);
    setValue(EMPLOYEE_SCHEDULE_FIELDS.ISO_WEEK, null);
    setValue(EMPLOYEE_SCHEDULE_FIELDS.AVAILABILITY_MINUTES, null);
    clearErrors(TASK_ASSIGNMENT_ERRORS.EMPLOYEE);
  };

  const handleEmployeeSelection = async (selectedEmployeeId) => {
    if (selectedEmployeeId === employeeId) return;

    isSelectingRef.current = true;

    setValue(EMPLOYEE_SCHEDULE_FIELDS.EMPLOYEE_ID, selectedEmployeeId, {
      shouldDirty: true,
      shouldValidate: true,
    });

    try {
      const isoWeek = await openModal(selectedEmployeeId);

      if (!isoWeek) {
        resetEmployeeSelection();
        return;
      }

      setValue(EMPLOYEE_SCHEDULE_FIELDS.ISO_WEEK, isoWeek, {
        shouldDirty: true,
        shouldValidate: true,
      });
    } finally {
      isSelectingRef.current = false;
    }
  };

  const selectedEmployee = employeeId ? employeeMap.get(employeeId) : null;

  useEffect(() => {
    if (disabled) return;

    if (
      employeeId &&
      !isoWeek &&
      !disabled &&
      !open &&
      !isSelectingRef.current &&
      employeeMap.has(employeeId)
    ) {
      openModal(employeeId);
    }
  }, [employeeId, isoWeek, disabled, open, openModal, employeeMap]);

  useEffect(() => {
    if (selectedAvailability?.isAvailable) {
      setValue(
        EMPLOYEE_SCHEDULE_FIELDS.AVAILABILITY_MINUTES,
        selectedAvailability?.remainingHours * 60,
        { shouldDirty: false, shouldValidate: false },
      );
    } else {
      setValue(EMPLOYEE_SCHEDULE_FIELDS.AVAILABILITY_MINUTES, null, {
        shouldDirty: false,
        shouldValidate: false,
      });
    }
  }, [selectedAvailability, setValue]);

  useEffect(() => {
    if (employeeId && isoWeek) {
      clearErrors(TASK_ASSIGNMENT_ERRORS.EMPLOYEE);
    }
  }, [employeeId, isoWeek, clearErrors]);

  return (
    <>
      <BaseSelect
        id={EMPLOYEE_SCHEDULE_FIELDS.EMPLOYEE_ID}
        value={employeeId ?? EMPTY_SINGLE_VALUE}
        options={options}
        disabled={disabled}
        onChange={(value) => {
          if (disabled) return;
          handleEmployeeSelection(value);
        }}
        placeholder='Select employee'
        renderValue={(value) => {
          const employee = employeeMap.get(value) as any;
          if (!employee) return 'Select employee';
          if (!isoWeek) return employee.name;

          if (selectedAvailability) {
            return (
              <span>
                <span>{employee.name}</span>
                <span className='text-sm text--secondary'>
                  {selectedAvailability?.remainingHours
                    ? ` (${selectedAvailability.remainingHours} hours available)`
                    : ''}
                </span>
              </span>
            );
          }

          return employee.name;
        }}
        renderOption={(opt) => {
          const employee = employeeMap.get(opt.value) as any;
          if (!employee) return null;
          const isSelectedEmployee = employee.id === employeeId;

          const availability =
            isSelectedEmployee && isoWeek ? getAvailabilityForWeek(isoWeek) : null;

          return (
            <EmployeeAssignmentOption
              employee={employee}
              availability={availability}
              isSelected={isSelectedEmployee}
              onOpenScheduler={() => openModal(employee.id)}
              disabled={disabled}
            />
          );
        }}
        sx={{ minWidth: 320 }}
        error={!!employeeErrorMessage}
        errorMessage={employeeErrorMessage?.message}
        fullWidth
        label='Employee'
        orientation='row'
        wrapperSx={{ '& > div': { columnGap: '32px' } }}
      />

      {!disabled && (
        <ModalWrapper open={open} onClose={close} maxWidth='960px' sx={{ padding: 5 }}>
          <EmployeeTaskScheduler
            selectedEmployee={selectedEmployee}
            onClose={close}
            onConfirm={(isoWeek) => confirm(isoWeek)}
            availabilityLoading={employeeAvailabilityLoading}
            getAvailabilityForWeek={getAvailabilityForWeek}
          />
        </ModalWrapper>
      )}
    </>
  );
}

/**
 * @param {Object} props
 * @param {{ id: string, name: string }} props.employee
 * @param {{ remainingHours: number, isAvailable: boolean } | null} props.availability
 * @param {boolean} props.isSelected
 * @param {() => void} props.onOpenScheduler
 * @param {boolean} props.disabled
 */
function EmployeeAssignmentOption({
  employee,
  availability,
  isSelected,
  onOpenScheduler,
  disabled,
}) {
  return (
    <div className='row row--between gap-5 flex-1'>
      <span className='font-medium'>{employee.name}</span>

      <span className='text-sm text--secondary'>
        {isSelected && availability?.isAvailable
          ? `${availability.remainingHours} hours left`
          : null}
      </span>

      <button
        className='btn text-btn text-btn--small underline'
        type='button'
        onClick={(e) => {
          e.stopPropagation();
          onOpenScheduler();
        }}
        disabled={disabled}
      >
        view schedule
      </button>
    </div>
  );
}
